/**
 * 渠道授权相关常量
 */

/** 抖音企业号渠道 key（后端渠道列表中的 key 字段） */
export const CHANNEL_KEY_DOUYIN = 'dy';

/** 微信公众号渠道 key */
export const CHANNEL_KEY_WECHAT_OFFICIAL = 'wx';

/** 微信客服渠道 key */
export const CHANNEL_KEY_WECHAT_SERVICE = 'wxkf';

/** 小红书渠道 key */
export const CHANNEL_KEY_XIAOHONGSHU = 'xhs';

/** 快手渠道 key */
export const CHANNEL_KEY_KUAISHOU = 'kuaishou';

/** 网站咨询页渠道 key */
export const CHANNEL_KEY_WEBSITE = 'web';

/** 腾讯咨询页渠道 key */
export const CHANNEL_KEY_TENCENT = 'fmc';

/** 百度营销渠道 key */
export const CHANNEL_KEY_BAIDU = 'baidu';

/** 企业微信（个人号）渠道 key */
export const CHANNEL_KEY_QYWX = 'qywx';

/** 抖音飞鸽（巨量引擎 ISV 医疗）渠道 key */
export const CHANNEL_KEY_DY_FEIGE = 'dy_feige';

/** 美团渠道 key */
export const CHANNEL_KEY_MEITUAN = 'meituan';

/** 抖音飞鸽接入操作指引文档（授权页说明与授权弹窗共用），统一走自建文档站 */
export { DOC_DY_FEIGE_AUTH as DY_FEIGE_GUIDE_URL } from '@/constants/docLinks';

/** 支持 OAuth 授权的渠道 key 列表 */
export const OAUTH_SUPPORTED_CHANNEL_KEYS = ['dy', 'xhs', 'kuaishou'] as const;

export function isOAuthSupportedChannel(key: string | undefined): boolean {
  return OAUTH_SUPPORTED_CHANNEL_KEYS.includes(
    key as (typeof OAUTH_SUPPORTED_CHANNEL_KEYS)[number],
  );
}

/** 渠道账号列表「开关状态」列说明：接待状态与 AI 状态合并后的表头提示（问号图标） */
export const ACCOUNT_SWITCH_STATUS_TIP =
  '接待状态、AI状态集中展示，可直接切换；关闭接待后该账号不再接收进线消息，关闭AI后消息只入库、不再由AI回复。';

/** 账号授权状态：1=未授权 2=已授权 3=已过期 4=已解绑 */
export const AUTH_STATUS = {
  UNAUTHORIZED: 1,
  AUTHORIZED: 2,
  EXPIRED: 3,
  UNBOUND: 4,
} as const;

/** refresh_token 状态：0=正常 1=需手动刷新 2=需重新授权 */
export const REFRESH_STATUS = {
  NORMAL: 0,
  NEED_REFRESH: 1,
  NEED_REAUTH: 2,
} as const;

/** 授权状态文案 */
export function formatAuthStatus(status?: number | null): string {
  switch (status) {
    case AUTH_STATUS.UNAUTHORIZED:
      return '未授权';
    case AUTH_STATUS.AUTHORIZED:
      return '已授权';
    case AUTH_STATUS.EXPIRED:
      return '已过期';
    case AUTH_STATUS.UNBOUND:
      return '已解绑';
    default:
      return '-';
  }
}

/** 渠道 key -> 管理页路由 path 映射 */
export const CHANNEL_KEY_TO_ROUTE: Record<string, string> = {
  [CHANNEL_KEY_DOUYIN]: '/channel-auth/douyin',
  [CHANNEL_KEY_WECHAT_OFFICIAL]: '/channel-auth/wechat-official',
  [CHANNEL_KEY_WECHAT_SERVICE]: '/channel-auth/wechat-service',
  [CHANNEL_KEY_XIAOHONGSHU]: '/channel-auth/xiaohongshu',
  [CHANNEL_KEY_KUAISHOU]: '/channel-auth/kuaishou',
  [CHANNEL_KEY_WEBSITE]: '/channel-auth/website',
  [CHANNEL_KEY_TENCENT]: '/channel-auth/tencent',
  [CHANNEL_KEY_BAIDU]: '/channel-auth/baidu',
  [CHANNEL_KEY_QYWX]: '/channel-auth/qywx',
  [CHANNEL_KEY_DY_FEIGE]: '/channel-auth/dy-feige',
  [CHANNEL_KEY_MEITUAN]: '/channel-auth/meituan',
};

/** 美团账号类型：main=总店 sub=总店子号 store=门店号 */
export const MEITUAN_ACCOUNT_TYPE = {
  MAIN: 'main',
  SUB: 'sub',
  STORE: 'store',
} as const;

/** 美团账号类型文案：优先后端 account_type_text，缺失时按 account_type 兜底 */
export function formatMeituanAccountType(
  accountType?: string | null,
  accountTypeText?: string | null,
): string {
  if (accountTypeText) return accountTypeText;
  switch (accountType) {
    case MEITUAN_ACCOUNT_TYPE.MAIN:
      return '总店';
    case MEITUAN_ACCOUNT_TYPE.SUB:
      return '总店子号';
    case MEITUAN_ACCOUNT_TYPE.STORE:
      return '门店号';
    default:
      return '-';
  }
}

/**
 * 跳转到渠道授权页时携带的账号查询参数（见 useChannelAuthAccountQuery）。
 * 授权异常提醒点击后带上其中之一，页面预填对应筛选并查询：
 * 直播授权掉线带账号ID，其余带账号名称。
 */
export const CHANNEL_ACCOUNT_EXTERNAL_ID_QUERY = 'account_external_id';
export const CHANNEL_ACCOUNT_NAME_QUERY = 'account_name';

/** 飞鸽账号类型筛选入参 is_parent_account：1=总店号 0=广告主账户 */
export const FEIGE_IS_PARENT_ACCOUNT = {
  PARENT: 1,
  ADVERTISER: 0,
} as const;

/** 飞鸽账号类型文案：按 parent_account_id 判断，为 0 是授权产生的总店号，非 0 是其下的广告主账户 */
export function formatFeigeAccountType(parentAccountId?: number | null): string {
  return Number(parentAccountId) > 0 ? '广告主账户' : '总店号';
}

/**
 * 飞鸽授权状态文案。复用通用 auth_status，但解绑态按飞鸽业务叫「已解除授权」。
 */
export function formatFeigeAuthStatus(status?: number | null): string {
  if (status === AUTH_STATUS.UNBOUND) return '已解除授权';
  return formatAuthStatus(status);
}

/**
 * 企微账号的设备绑定状态文案。
 * 复用 auth_status 字段但语义不同：1=未绑定设备（不收消息）2=已绑定设备。
 */
export function formatQywxBindStatus(status?: number | null): string {
  if (status === AUTH_STATUS.AUTHORIZED) return '已绑定';
  if (status === AUTH_STATUS.UNAUTHORIZED) return '未绑定';
  return '-';
}
